import { useCallback, useState } from "react";
import { Check, Link2 } from "../compose/Icon";
import { buildCitationUrl } from "../lib/permalink";

/**
 * A reusable "Copy link" action for a review comment (Phase-2 permalink-citations
 * chunk). Yields the STABLE permalink `/w/:slug/doc/:id?comment=<id>` (absolute,
 * clipboard-ready) that deep-links straight to the cited text — the "linkable
 * citations that survive edits" affordance. Drop it into any comment card /
 * thread header; it is self-contained (no DocumentViewer coupling).
 *
 * Mirrors the elaborate-owned `HashChip` clipboard pattern: optimistic copy via
 * `navigator.clipboard`, a transient "Copied" confirmation, and an `onCopied`
 * hook for the host toast. The built URL is exposed as `data-permalink` for
 * deep-link E2E assertions.
 */
export interface CopyLinkButtonProps {
  slug: string;
  docId: string;
  commentId: number;
  /** Defaults to `window.location.origin` (single-origin app). */
  origin?: string;
  label?: string;
  /** Icon-only (no text label) — the comment-card Row-1 bar (rich-comments §11). */
  compact?: boolean;
  className?: string;
  onCopied?: (url: string) => void;
}

export function CopyLinkButton({
  slug,
  docId,
  commentId,
  origin,
  label = "Copy link",
  compact = false,
  className,
  onCopied,
}: CopyLinkButtonProps) {
  const [copied, setCopied] = useState(false);
  const resolvedOrigin =
    origin ?? (typeof window !== "undefined" ? window.location.origin : "");
  const url = buildCitationUrl(resolvedOrigin, slug, docId, commentId);

  const onClick = useCallback(() => {
    void navigator.clipboard?.writeText(url).catch(() => {});
    setCopied(true);
    onCopied?.(url);
    window.setTimeout(() => setCopied(false), 2000);
  }, [url, onCopied]);

  const cls = [
    "btn",
    "btn--ghost",
    "btn--sm",
    "copy-link",
    compact ? "copy-link--compact" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={cls}
      data-testid={`copy-link-${commentId}`}
      data-permalink={url}
      title={compact ? "Copy a permalink to this comment" : url}
      aria-label="Copy a permalink to this comment"
      onClick={onClick}
    >
      {copied ? (
        <Check size={14} strokeWidth={1.5} />
      ) : (
        <Link2 size={14} strokeWidth={1.5} />
      )}
      {compact ? null : <span>{copied ? "Copied" : label}</span>}
    </button>
  );
}
